/**
 * Partner Portal role enums
 * @see Issue #238 - Partner Companies B2B Model
 */

/**
 * Roles within a partner company (for Partner Portal RBAC)
 */
export enum PartnerRole {
  OWNER = 'owner', // Full access, company settings
  BRANCH_MANAGER = 'branch_manager', // Branch team + performance
  LOAN_OFFICER = 'loan_officer', // Own referrals + commissions
  PROCESSOR = 'processor', // View deals, support
  ADMIN = 'admin', // Team management, no financials
  VIEWER = 'viewer', // Read-only dashboard
}

/**
 * Partner role display names
 */
export const PARTNER_ROLE_NAMES: Record<PartnerRole, string> = {
  [PartnerRole.OWNER]: 'Owner',
  [PartnerRole.BRANCH_MANAGER]: 'Branch Manager',
  [PartnerRole.LOAN_OFFICER]: 'Loan Officer',
  [PartnerRole.PROCESSOR]: 'Processor',
  [PartnerRole.ADMIN]: 'Admin',
  [PartnerRole.VIEWER]: 'Viewer',
};

/**
 * Partner role descriptions
 */
export const PARTNER_ROLE_DESCRIPTIONS: Record<PartnerRole, string> = {
  [PartnerRole.OWNER]: 'Full access to all features and company settings',
  [PartnerRole.BRANCH_MANAGER]: 'Manage branch team and view branch performance',
  [PartnerRole.LOAN_OFFICER]: 'Submit referrals and view own commissions',
  [PartnerRole.PROCESSOR]: 'View deals and provide support',
  [PartnerRole.ADMIN]: 'Manage team members, no financial access',
  [PartnerRole.VIEWER]: 'Read-only access to dashboard',
};

/**
 * Partner role hierarchy for permission checks
 * Higher index = more permissions
 */
export const PARTNER_ROLE_HIERARCHY: readonly PartnerRole[] = [
  PartnerRole.VIEWER,
  PartnerRole.PROCESSOR,
  PartnerRole.LOAN_OFFICER,
  PartnerRole.ADMIN,
  PartnerRole.BRANCH_MANAGER,
  PartnerRole.OWNER,
] as const;

/**
 * Check if roleA has equal or higher permissions than roleB
 */
export function hasEqualOrHigherPartnerRole(roleA: PartnerRole, roleB: PartnerRole): boolean {
  return PARTNER_ROLE_HIERARCHY.indexOf(roleA) >= PARTNER_ROLE_HIERARCHY.indexOf(roleB);
}

/**
 * Permission matrix for Partner Portal features
 */
export const PARTNER_PERMISSIONS = {
  viewDashboard: [
    PartnerRole.OWNER,
    PartnerRole.BRANCH_MANAGER,
    PartnerRole.LOAN_OFFICER,
    PartnerRole.PROCESSOR,
    PartnerRole.ADMIN,
    PartnerRole.VIEWER,
  ],
  submitReferrals: [PartnerRole.OWNER, PartnerRole.BRANCH_MANAGER, PartnerRole.LOAN_OFFICER],
  viewAllCommissions: [PartnerRole.OWNER],
  viewBranchCommissions: [PartnerRole.BRANCH_MANAGER],
  viewOwnCommissions: [PartnerRole.LOAN_OFFICER],
  manageTeam: [PartnerRole.OWNER, PartnerRole.ADMIN],
  manageBranchTeam: [PartnerRole.BRANCH_MANAGER],
  manageAgreements: [PartnerRole.OWNER],
  manageCompanySettings: [PartnerRole.OWNER],
  viewReports: [PartnerRole.OWNER, PartnerRole.BRANCH_MANAGER, PartnerRole.ADMIN, PartnerRole.VIEWER],
  uploadCompliance: [PartnerRole.OWNER, PartnerRole.BRANCH_MANAGER, PartnerRole.ADMIN],
  uploadOwnCompliance: [PartnerRole.LOAN_OFFICER],
} as const;
